// 코워크 작업공간 파일(목록·업로드·다운로드·삭제) + 코워크 토글. core의 프로젝트 선택/코워크 상태를 받아 동작.
import { useEffect, useState, useRef } from 'react';
import { chatAPI } from '../../../api/client';
import { alertDialog, confirmDialog } from '../../../components/AppDialog';

export const WORKSPACE_MAX_UPLOAD_MB = 100;
export const WORKSPACE_MAX_UPLOAD_BYTES = WORKSPACE_MAX_UPLOAD_MB * 1024 * 1024;

const ROLE_RANK = { none: 0, viewer: 1, editor: 2, owner: 3 };
const CAPABILITY_ROLE = {
  list: 'viewer',
  read: 'viewer',
  download: 'viewer',
  upload: 'editor',
  write: 'editor',
  delete: 'editor',
};
const CAPABILITY_LABEL = {
  list: '목록 조회',
  read: '읽기',
  download: '다운로드',
  upload: '업로드',
  write: '쓰기',
  delete: '삭제',
};

export const normalizeWorkspaceRole = (role) => (
  ['owner', 'editor', 'viewer'].includes(role) ? role : 'none'
);

export const workspaceRoleCan = (role, capability) => (
  (ROLE_RANK[normalizeWorkspaceRole(role)] || 0) >= (ROLE_RANK[CAPABILITY_ROLE[capability]] || ROLE_RANK.owner)
);

export const workspaceRestrictionReason = (role, capability) => {
  const normalized = normalizeWorkspaceRole(role);
  const required = CAPABILITY_ROLE[capability] || 'owner';
  return `${normalized} 역할은 작업공간 ${CAPABILITY_LABEL[capability] || capability} 기능을 사용할 수 없습니다. ${required} 이상 권한이 필요합니다.`;
};

export default function useWorkspace({
  selectedProjectId,
  cowork,
  setCowork,
  downloadBlob,
  accessRole = 'owner',
  canWorkspaceRead,
  canWorkspaceDownload,
  canWorkspaceUpload,
  canWorkspaceDelete,
}) {
  const [workspaceFiles, setWorkspaceFiles] = useState([]);        // 작업공간 파일 목록
  const [workspaceBusy, setWorkspaceBusy] = useState(false);       // 작업공간 파일 작업 중 표시
  const wsFileRef = useRef(null);   // 작업공간 파일 업로드 input
  const workspaceRequestRef = useRef(0);
  const selectedProjectIdRef = useRef(selectedProjectId || null);
  const canRead = canWorkspaceRead ?? workspaceRoleCan(accessRole, 'read');
  const canDownload = canWorkspaceDownload ?? workspaceRoleCan(accessRole, 'download');
  const canUpload = canWorkspaceUpload ?? workspaceRoleCan(accessRole, 'upload');
  const canDelete = canWorkspaceDelete ?? workspaceRoleCan(accessRole, 'delete');

  useEffect(() => {
    selectedProjectIdRef.current = selectedProjectId || null;
    workspaceRequestRef.current++;
    setWorkspaceBusy(false);
  }, [selectedProjectId]);

  const sameProject = (a, b) => String(a || '') === String(b || '');
  const isCurrentProject = (pid) => sameProject(selectedProjectIdRef.current, pid);
  const applyWorkspaceFiles = (requestId, pid, files) => {
    if (workspaceRequestRef.current !== requestId || !isCurrentProject(pid)) return;
    setWorkspaceFiles(files);
  };
  const resetWorkspaceForProject = (pid = null) => {
    selectedProjectIdRef.current = pid || null;
    workspaceRequestRef.current++;
    setWorkspaceBusy(false);
    setWorkspaceFiles([]);
  };

  const loadWorkspaceFiles = (pid) => {
    const targetProjectId = pid || null;
    const requestId = ++workspaceRequestRef.current;
    if (!targetProjectId) {
      setWorkspaceFiles([]);
      return Promise.resolve([]);
    }
    if (!canRead) {
      setWorkspaceFiles([]);
      return Promise.resolve([]);
    }
    setWorkspaceFiles([]);
    return chatAPI.workspaceList(targetProjectId)
      .then((r) => {
        const files = r.data.files || [];
        applyWorkspaceFiles(requestId, targetProjectId, files);
        return files;
      })
      .catch(() => {
        applyWorkspaceFiles(requestId, targetProjectId, []);
        return [];
      });
  };
  const toggleCowork = () => {
    if (!selectedProjectId || !canRead) return;
    const next = !cowork;
    setCowork(next);
    if (next) loadWorkspaceFiles(selectedProjectId);
  };
  const uploadWorkspaceFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const targetProjectId = selectedProjectId;
    if (!files.length || !targetProjectId) return;
    if (!canUpload) {
      alertDialog(workspaceRestrictionReason(accessRole, 'upload'));
      return;
    }
    setWorkspaceBusy(true);
    const failed = [];
    for (const file of files) {
      if (file.size > WORKSPACE_MAX_UPLOAD_BYTES) {
        failed.push(`${file.name}: 파일은 ${WORKSPACE_MAX_UPLOAD_MB}MB를 초과할 수 없습니다.`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      try {
        await chatAPI.workspaceUpload(targetProjectId, fd);
      } catch (err) {
        const detail = err.response?.data?.detail;
        const message = detail?.reason || detail?.message || detail;
        failed.push(file.name + (message ? `: ${message}` : ''));
      }
    }
    if (isCurrentProject(targetProjectId)) setWorkspaceBusy(false);
    loadWorkspaceFiles(targetProjectId);
    if (failed.length) alertDialog(`일부 파일 업로드 실패:\n${failed.join('\n')}`);
  };
  const downloadWorkspaceFile = async (name) => {
    const targetProjectId = selectedProjectId;
    if (!targetProjectId) return;
    if (!canDownload) {
      alertDialog(workspaceRestrictionReason(accessRole, 'download'));
      return;
    }
    try {
      const r = await chatAPI.workspaceDownload(targetProjectId, name);
      downloadBlob(r.data, name);
    } catch { alertDialog('다운로드에 실패했습니다.'); }
  };
  const deleteWorkspaceFile = async (name) => {
    const targetProjectId = selectedProjectId;
    if (!targetProjectId) return;
    if (!canDelete) {
      alertDialog(workspaceRestrictionReason(accessRole, 'delete'));
      return;
    }
    if (!(await confirmDialog(`'${name}' 파일을 삭제하시겠습니까?`))) return;
    try {
      await chatAPI.workspaceDelete(targetProjectId, name);
      loadWorkspaceFiles(targetProjectId);
    } catch { alertDialog('삭제에 실패했습니다.'); }
  };

  return {
    workspaceFiles, setWorkspaceFiles, workspaceBusy, setWorkspaceBusy, wsFileRef,
    resetWorkspaceForProject,
    loadWorkspaceFiles, toggleCowork, uploadWorkspaceFile, downloadWorkspaceFile, deleteWorkspaceFile,
    canWorkspaceRead: canRead, canWorkspaceDownload: canDownload,
    canWorkspaceUpload: canUpload, canWorkspaceDelete: canDelete,
  };
}
